import { Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum } from "@/lib/format";
import { 
  Package, TrendingUp, DollarSign, Search, 
  Calendar, PieChart as PieIcon, BarChart3, Info, AlertCircle, Layers 
} from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  CartesianGrid, PieChart, Pie, Cell, Legend, LineChart, Line
} from "recharts";

interface ProdutoAgregado {
  xProd: string;
  ncm: string;
  cfop: string;
  qtd: number;
  vTotal: number;
  vTribTotal: number;
  cargaTributaria: number;
  ocorrencias: number;
  cluster?: 'A' | 'B' | 'C';
}

const DIAS_SEMANA = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

export default function ProdutosPage() {
  const { notas, dashboard } = useFiscalStore();
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState<keyof ProdutoAgregado>("vTotal");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  useEffect(() => { document.title = "Insights de Produtos — Extrator Fiscal"; }, []);

  const insights = useMemo(() => {
    const pMap = new Map<string, ProdutoAgregado>();
    const dMap = new Array(7).fill(0).map((_, i) => ({ day: DIAS_SEMANA[i], valor: 0, qtd: 0, impostos: 0 }));
    let vTotalProd = 0;
    let vTotalTrib = 0;

    for (const nota of notas) {
      const data = new Date(nota.emissao);
      const diaSemana = isNaN(data.getTime()) ? -1 : data.getDay();
      
      for (const item of nota.itens ?? []) {
        const key = `${item.ncm}||${item.xProd}`;
        const vItem = item.vProd || 0;
        const vTrib = (item.vICMS || 0) + (item.vPIS || 0) + (item.vCOFINS || 0) + (item.vIPI || 0);
        vTotalProd += vItem;
        vTotalTrib += vTrib;

        if (diaSemana !== -1) {
          dMap[diaSemana].valor += vItem;
          dMap[diaSemana].qtd += item.qCom;
          dMap[diaSemana].impostos += vTrib;
        }

        const existing = pMap.get(key);
        if (existing) {
          existing.qtd += item.qCom;
          existing.vTotal += vItem;
          existing.vTribTotal += vTrib;
          existing.ocorrencias += 1;
        } else {
          pMap.set(key, {
            xProd: item.xProd,
            ncm: item.ncm,
            cfop: item.cfop,
            qtd: item.qCom,
            vTotal: vItem,
            vTribTotal: vTrib,
            cargaTributaria: 0,
            ocorrencias: 1,
          });
        }
      }
    }

    // Calcula Carga Tributária e Clusterização ABC
    const sorted = [...pMap.values()].sort((a, b) => b.vTotal - a.vTotal);
    let cumulative = 0;
    const clusters = { A: 0, B: 0, C: 0 };

    sorted.forEach(p => {
      p.cargaTributaria = p.vTotal > 0 ? (p.vTribTotal / p.vTotal) * 100 : 0;
      cumulative += p.vTotal;
      const ratio = vTotalProd > 0 ? cumulative / vTotalProd : 0;
      if (ratio <= 0.8) { p.cluster = 'A'; clusters.A += p.vTotal; }
      else if (ratio <= 0.95) { p.cluster = 'B'; clusters.B += p.vTotal; }
      else { p.cluster = 'C'; clusters.C += p.vTotal; }
    });

    return {
      produtos: sorted,
      diasSemana: dMap,
      abcData: [
        { name: "Classe A (80%)", value: clusters.A, color: "#1A66CC" },
        { name: "Classe B (15%)", value: clusters.B, color: "#33CCFF" },
        { name: "Classe C (5%)", value: clusters.C, color: "#94a3b8" },
      ],
      vTotalProd,
      vTotalTrib,
      cargaMedia: vTotalProd > 0 ? (vTotalTrib / vTotalProd) * 100 : 0
    };
  }, [notas]);

  const filtrados = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return insights.produtos
      .filter((p) => !ql || p.xProd.toLowerCase().includes(ql) || p.ncm.includes(ql) || p.cfop.includes(ql))
      .sort((a, b) => {
        const va = a[sortKey] ?? 0;
        const vb = b[sortKey] ?? 0;
        return sortDir === "desc" ? (vb as number) - (va as number) : (va as number) - (vb as number);
      });
  }, [insights.produtos, q, sortKey, sortDir]);

  const slice = filtrados.slice(page * itemsPerPage, (page + 1) * itemsPerPage);
  const totalPages = Math.max(1, Math.ceil(filtrados.length / itemsPerPage));

  const top30 = useMemo(() => 
    insights.produtos.slice(0, 30).map(p => ({
      name: p.xProd.length > 25 ? p.xProd.slice(0, 25) + "..." : p.xProd,
      valor: p.vTotal,
      carga: p.cargaTributaria
    }))
  , [insights.produtos]);

  const toggleSort = (key: keyof ProdutoAgregado) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  if (!dashboard) return <Navigate to="/upload" replace />;

  return (
    <div className="px-8 py-8 max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight dark:text-white">Inteligência de Produtos</h1>
          <p className="text-sm text-muted-foreground mt-1">Análise volumétrica, carga tributária e clusterização ABC de faturamento.</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl">
          <Calendar className="size-4 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-medium text-blue-900 dark:text-blue-200">{fmtNum(notas.length)} Notas Analisadas</span>
        </div>
      </header>

      {/* KPIs de Destaque com Design Glass */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="animate-in-card" style={{ animationDelay: '100ms' }}>
          <KpiCard label="Faturamento Total" value={fmtBRL(insights.vTotalProd)} icon={DollarSign} color="text-emerald-500" />
        </div>
        <div className="animate-in-card" style={{ animationDelay: '200ms' }}>
          <KpiCard label="Total Tributos" value={fmtBRL(insights.vTotalTrib)} icon={Layers} color="text-amber-500" />
        </div>
        <div className="animate-in-card" style={{ animationDelay: '300ms' }}>
          <KpiCard label="Carga Tributária" value={`${insights.cargaMedia.toFixed(2)}%`} icon={TrendingUp} color="text-red-500" />
        </div>
        <div className="animate-in-card" style={{ animationDelay: '400ms' }}>
          <KpiCard label="Produtos Únicos" value={fmtNum(insights.produtos.length)} icon={Package} color="text-blue-500" />
        </div>
        <div className="animate-in-card" style={{ animationDelay: '500ms' }}>
          <KpiCard label="Qtd Classe A" value={fmtNum(insights.produtos.filter(p => p.cluster === 'A').length)} icon={PieIcon} color="text-purple-500" />
        </div>
      </div>

      {/* Grid de Gráficos */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Gráfico Sazonalidade Semanal */}
        <div className="glass-card p-6 animate-in-card hover-glow" style={{ animationDelay: '600ms' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold flex items-center gap-2"><BarChart3 className="size-5 text-primary" /> Faturamento e Impostos Sazonais</h3>
            <span className="text-[10px] uppercase font-bold text-muted-foreground bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">Volume por Dia</span>
          </div>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={insights.diasSemana}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-10" />
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11 }} tickFormatter={v => `R$ ${v/1000}k`} />
                <Tooltip 
                  formatter={(v: number, name: string) => [fmtBRL(v), name === 'valor' ? 'Faturamento' : 'Impostos']}
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                />
                <Legend verticalAlign="top" height={36} iconType="circle"/>
                <Bar dataKey="valor" name="Faturamento" fill="#10B981" radius={[4, 4, 0, 0]} barSize={30} />
                <Bar dataKey="impostos" name="Impostos" fill="#EF4444" radius={[4, 4, 0, 0]} barSize={30} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gráfico Curva ABC */}
        <div className="glass-card p-6 animate-in-card hover-glow" style={{ animationDelay: '700ms' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold flex items-center gap-2"><PieIcon className="size-5 text-primary" /> Distribuição ABC (Curva de Pareto)</h3>
            <div className="group relative">
              <Info className="size-4 text-muted-foreground cursor-help" />
              <div className="absolute right-0 bottom-full mb-2 w-64 p-3 bg-slate-900 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                A: 80% do Faturamento (Foco total)<br/>
                B: 15% do Faturamento (Intermediário)<br/>
                C: 5% do Faturamento (Cauda Longa)
              </div>
            </div>
          </div>
          <div className="h-[280px] flex items-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie 
                  data={insights.abcData} 
                  innerRadius={70} 
                  outerRadius={95} 
                  paddingAngle={5} 
                  dataKey="value"
                  stroke="none"
                >
                  {insights.abcData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => fmtBRL(v)} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                <Legend verticalAlign="bottom" height={36} iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top 30 e Tabela */}
      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-1 glass-card p-6 h-fit flex flex-col animate-in-card hover-glow" style={{ animationDelay: '800ms' }}>
          <div className="flex items-center justify-between mb-2 shrink-0">
            <h3 className="font-bold flex items-center gap-2"><TrendingUp className="size-5 text-primary" /> Ranking Top 30</h3>
            <div className="flex items-center gap-2 text-[8px] font-bold uppercase tracking-tighter">
              <span className="flex items-center gap-1 text-red-500"><div className="size-1.5 rounded-full bg-red-500"></div> &gt;20%</span>
              <span className="flex items-center gap-1 text-amber-500"><div className="size-1.5 rounded-full bg-amber-500"></div> &le;20%</span>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mb-4 font-bold uppercase tracking-widest opacity-70">Carga Tributária (Compras/Vendas)</p>
          <div className="flex flex-col gap-2 overflow-hidden pr-1">
            {top30.map((p, i) => (
              <div key={i} className="flex flex-col gap-0 group border-b border-white/5 pb-1 last:border-0 justify-center">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-black text-muted-foreground w-5 shrink-0">{i + 1}</span>
                    <span className="text-[12px] font-bold truncate flex-1 dark:text-slate-200" title={p.name}>{p.name}</span>
                  </div>
                  <div className="text-[12px] font-black text-emerald-500 tabular-nums">{fmtBRL(p.valor)}</div>
                </div>
                <div className="flex items-center justify-end">
                  <span className={`text-[10px] font-bold ${p.carga > 20 ? 'text-red-500' : 'text-amber-500'}`}>
                    {p.carga.toFixed(1)}% <span className="text-[8px] opacity-60 uppercase ml-1 font-extrabold tracking-tighter">Carga Trib.</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4 animate-in-card" style={{ animationDelay: '900ms' }}>
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input 
                value={q} 
                onChange={(e) => { setQ(e.target.value); setPage(0); }}
                placeholder="Pesquisar por Nome, NCM ou CFOP do produto..."
                className="w-full pl-10 pr-4 py-3 rounded-xl text-sm bg-white/5 border border-border focus:ring-2 focus:ring-primary/50 outline-none transition-all dark:text-white glass"
              />
            </div>
            {q && <button onClick={() => setQ("")} className="text-xs text-primary font-bold px-3">Limpar</button>}
          </div>

          <div className="glass-card overflow-hidden h-full flex flex-col">
            <div className="overflow-auto flex-1 custom-scrollbar">
              <table className="w-full text-sm whitespace-nowrap">
                <thead className="bg-slate-50/5 dark:bg-slate-900/50">
                  <tr className="text-muted-foreground">
                    <th className="text-left p-4 font-bold text-[10px] uppercase">Cluster</th>
                    <th className="text-left p-4 font-bold text-[10px] uppercase">Produto</th>
                    <th className="text-left p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary" onClick={() => toggleSort("ncm")}>
                      NCM {sortKey === 'ncm' && (sortDir === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary" onClick={() => toggleSort("qtd")}>
                      Qtd {sortKey === 'qtd' && (sortDir === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary text-amber-500" onClick={() => toggleSort("cargaTributaria")}>
                      Carga Trib. % {sortKey === 'cargaTributaria' && (sortDir === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary text-emerald-500" onClick={() => toggleSort("vTotal")}>
                      Receita Total {sortKey === 'vTotal' && (sortDir === 'desc' ? '↓' : '↑')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {slice.map((p, i) => (
                    <tr key={i} className="hover:bg-primary/5 transition-colors group">
                      <td className="p-4">
                        <span className={`text-[10px] font-extrabold px-2 py-1 rounded-md ${
                          p.cluster === 'A' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' :
                          p.cluster === 'B' ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300' :
                          'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                        }`}>
                          {p.cluster}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="font-semibold text-slate-900 dark:text-slate-200 max-w-[250px] truncate" title={p.xProd}>{p.xProd}</div>
                        <div className="text-[10px] opacity-60 flex gap-2">
                          <span>CFOP: {p.cfop}</span>
                        </div>
                      </td>
                      <td className="p-4 text-left tabular-nums font-mono text-xs text-blue-600 dark:text-blue-400 font-bold">{p.ncm}</td>
                      <td className="p-4 text-right tabular-nums text-muted-foreground">{fmtNum(p.qtd)}</td>
                      <td className="p-4 text-right font-medium text-amber-500 tabular-nums">{p.cargaTributaria.toFixed(1)}%</td>
                      <td className="p-4 text-right font-black text-emerald-500 tabular-nums">{fmtBRL(p.vTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Paginação */}
            <div className="p-4 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between bg-slate-50/50 dark:bg-transparent gap-4">
              <div className="flex items-center gap-4">
                <span className="text-xs text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <select 
                  value={itemsPerPage} 
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  className="bg-transparent border border-white/20 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-primary glass"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs text-muted-foreground">Exibindo {slice.length} de {filtrados.length}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground mr-2">Página {page + 1} de {totalPages}</span>
                <button 
                  disabled={page === 0} 
                  onClick={() => setPage(p => p - 1)}
                  className="px-3 py-1.5 rounded-lg border border-white/10 glass disabled:opacity-30 text-xs font-bold hover:bg-primary/10 transition-colors"
                >
                  Anterior
                </button>
                <button 
                  disabled={page >= totalPages - 1} 
                  onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1.5 rounded-lg border border-white/10 glass disabled:opacity-30 text-xs font-bold hover:bg-primary/10 transition-colors"
                >
                  Próximo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color }: { label: string; value: string; icon: any; color: string }) {
  return (
    <div className="glass-card p-4 hover-lift">
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg bg-slate-100 dark:bg-slate-800/50 ${color}`}>
          <Icon className="size-5" />
        </div>
      </div>
      <div>
        <p className="text-xs text-muted-foreground font-medium">{label}</p>
        <h3 className="text-xl font-bold mt-1 tabular-nums">{value}</h3>
      </div>
    </div>
  );
}
